import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Grid,
  Paper,
  Chip
} from '@mui/material';
import { EDUGATE_API_URL } from '../config/serviceConfig';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  DonutLarge as DonutLargeIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import UserCoursesPanel from './UserCoursesPanel';
import CourseEnrollmentDialog from './CourseEnrollmentDialog';
import '../styles/CourseManagement.css';

const CourseManagementDialog = ({ 
  open, 
  onClose, 
  currentUser,
  showNotification 
}) => {
  const { t } = useTranslation();
  const accent = currentUser?.branding?.themeColor || '#4D8BFF';
  // ... (保留原有的 getCasUserStudentId 和 getUserStudentId 函数逻辑不变) ...
  // 提示：原有的 getCasUserStudentId 和 getUserStudentId 函数逻辑可以直接复制保留，不需要修改

  // ---------------------------------------------------------------------------
  // 请确保保留原有的这两个辅助函数：getCasUserStudentId, getUserStudentId
  // ---------------------------------------------------------------------------
  const getCasUserStudentId = async (employeeNumber, userName, userType = 'bupt_cas', phone = '') => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Cas-User-ID': encodeURIComponent(employeeNumber),
        'X-User-Name': encodeURIComponent(userName),
        'X-User-Type': userType // ✅ 添加用户类型
      };
      
      const response = await fetch(`${EDUGATE_API_URL}/api/auth/get-student-id`, {
        method: 'POST',
        headers: headers
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          return result.data.student_id;
        }
      }
      return null;
    } catch (error) {
      console.error('获取CAS用户student_id失败:', error);
      return null;
    }
  };

  const getUserStudentId = async () => {
    if (currentUser.student_id) return currentUser.student_id;
    if (currentUser.isCasUser && currentUser.employeeNumber) {
      const studentId = await getCasUserStudentId(
        currentUser.employeeNumber, 
        currentUser.name,
        currentUser.user_type || 'bupt_cas'
      );
      if (studentId) {
        currentUser.student_id = studentId;
        return studentId;
      }
    }
    return '';
  };
  // ---------------------------------------------------------------------------

  const [currentTab, setCurrentTab] = useState(0);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [coursesRefreshKey, setCoursesRefreshKey] = useState(0);

  const handleCourseChange = () => {
    setCoursesRefreshKey(prev => prev + 1);
  };

  const handleJoinSuccess = () => {
    setShowJoinDialog(false);
    handleCourseChange();
    showNotification(t('course_join_success'), 'success');
  };

  const handleClose = () => {
    setCurrentTab(0);
    setShowJoinDialog(false);
    onClose();
  };

  const handleJoinNewCourse = () => {
    setShowJoinDialog(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        className="workbench-dialog course-management-dialog"
        PaperProps={{
          className: 'workbench-dialog-paper ybg-course-dialog',
          style: { '--workbench-accent': accent },
          sx: {
            height: 'min(760px, calc(100vh - 64px))',
            minHeight: 0,
            borderRadius: '10px',
            background: '#1e1e1e',
            boxShadow: '0 18px 48px rgba(0, 0, 0, 0.38)',
            border: '1px solid #3e3e42',
            color: '#e7e7e7',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle className="workbench-dialog-title" sx={{
          px: 3,
          py: 1.5,
          background: '#202124',
          borderTop: `2px solid ${accent}`,
          borderBottom: '1px solid #3e3e42',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SchoolIcon sx={{ color: accent, fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 650, letterSpacing: '0.01em' }}>
              {t('course_management')}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ 
              color: '#a6afb8',
              borderRadius: '6px',
              transition: 'background-color 0.16s ease, color 0.16s ease',
              '&:hover': { 
                color: '#fff', 
                background: '#303238',
                transform: 'none'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="workbench-dialog-content" sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* 标签页导航 */}
            <Box sx={{ px: 3, background: '#252526', borderBottom: '1px solid #3e3e42' }}>
              <Tabs 
                value={currentTab} 
                onChange={(e, newValue) => setCurrentTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': {
                    height: '2px',
                    background: accent
                  },
                  '& .MuiTab-root': {
                    color: '#a6afb8',
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: 550,
                    minHeight: '46px',
                    mr: 3,
                    px: 0,
                    '&.Mui-selected': { color: accent },
                    '&:hover': { color: '#e7e7e7' }
                  }
                }}
              >
                <Tab label={t('course_my_courses')} icon={<ClassIcon sx={{ fontSize: 20, mb: 0.5 }} />} iconPosition="start" />
                <Tab label={t('course_stats')} icon={<DonutLargeIcon sx={{ fontSize: 20, mb: 0.5 }} />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* 内容区域 */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              p: { xs: 2, sm: 3 },
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '3px' }
            }}>
              {currentTab === 0 ? (
                <UserCoursesPanel
                  key={coursesRefreshKey}
                  currentUser={currentUser}
                  showNotification={showNotification}
                  onCourseChange={handleCourseChange}
                  onJoinClick={handleJoinNewCourse}
                  accentColor={accent}
                />
              ) : (
                <CourseStatistics 
                  currentUser={currentUser}
                  refreshKey={coursesRefreshKey}
                  getUserStudentId={getUserStudentId}
                  accentColor={accent}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <CourseEnrollmentDialog
        open={showJoinDialog}
        onClose={() => setShowJoinDialog(false)}
        currentUser={currentUser}
        accentColor={accent}
        showNotification={(message, type) => {
          showNotification(message, type);
          if (type === 'success') handleJoinSuccess();
        }}
      />
    </>
  );
};

// 优化后的统计组件
const CourseStatistics = ({ currentUser, refreshKey, getUserStudentId, accentColor = '#4D8BFF' }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalCourses: 0, activeCourses: 0, totalTasks: 0, completedTasks: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentUser?.username) return;
      const studentId = await getUserStudentId();
      if (!studentId) return;

      setLoading(true);
      try {
        const [coursesRes, homeworkRes] = await Promise.all([
          fetch(`${EDUGATE_API_URL}/api/courses/user-enrollments?student_id=${studentId}`).then(r => r.json()),
          fetch(`${EDUGATE_API_URL}/api/homework/list?student_id=${studentId}`).then(r => r.json())
        ]);

        if (coursesRes.code === 0 && homeworkRes.code === 0) {
          const courses = coursesRes.data.enrollments || [];
          const homeworks = homeworkRes.data || [];
          setStats({
            totalCourses: courses.length,
            activeCourses: courses.filter(c => c.enrollment_status === 1).length,
            totalTasks: homeworks.length,
            completedTasks: homeworks.filter(hw => hw.status === '2' || hw.status === '已提交').length
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [currentUser, refreshKey]);

  const StatCard = ({ title, value, icon }) => (
    <Paper sx={{
      p: 2.5,
      borderRadius: '6px',
      background: '#292a2d',
      border: '1px solid #3e3e42',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      transition: 'background-color 0.16s ease, border-color 0.16s ease',
      '&:hover': { transform: 'none', background: '#303238', borderColor: accentColor }
    }}>
      <Box sx={{ 
        p: 1.5, 
        borderRadius: '6px',
        background: `${accentColor}1a`,
        color: accentColor,
        display: 'flex'
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 0.5 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 650, color: '#e7e7e7' }}>
          {loading ? '...' : value}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <StatCard title={t('course_joined')} value={stats.activeCourses} icon={<SchoolIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title={t('course_tasks')} value={stats.totalTasks} icon={<AssignmentIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title={t('course_completed')} value={stats.completedTasks} icon={<CheckCircleIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title={t('course_total')} value={stats.totalCourses} icon={<ClassIcon />} />
        </Grid>
      </Grid>

      {stats.totalTasks > 0 && (
        <Paper sx={{
          p: 3,
          borderRadius: '6px',
          background: '#292a2d',
          border: '1px solid #3e3e42'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#e7e7e7' }}>{t('course_task_progress')}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor }}>
              {Math.round((stats.completedTasks / stats.totalTasks) * 100)}%
            </Typography>
          </Box>
          <Box sx={{ height: '6px', borderRadius: '3px', background: '#3e3e42', overflow: 'hidden' }}>
            <Box sx={{
              height: '100%',
              background: accentColor,
              width: `${(stats.completedTasks / stats.totalTasks) * 100}%`,
              transition: 'width 1s ease-in-out'
            }} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default CourseManagementDialog;